import { createSlice } from '@reduxjs/toolkit';
import { AccountingState, PricingModel } from './models/pricing-model.model';
import {
  CreatableProviderServicePricing,
  EditableProviderServicePricing,
  ProviderServicePricing,
} from './models/provider-service-pricing.model';
import { PricingFunction } from './models/pricing-function.model';
import { TransportationService } from './models/transportation-service.model';
import accountingService from '../services/accountingService';
import * as uiSlice from './ui.slice';
import { TransportationAttribute } from './models/transportation-attribute.model';
import { TripLegPricings } from './models/trip-leg-pricings.model';

interface AccountState {
  // Pricing Functions
  allPricingFunctions: PricingFunction[];
  loadingPricingFunctions: boolean;
  loadedPricingFunctions: boolean;

  // Pricing Models
  allPricingModels: PricingModel[];
  activePricingModels: PricingModel[];
  loadingPricingModels: boolean;
  loadedPricingModels: boolean;
  savingPricingModel: boolean;
  savedPricingModel: boolean;

  // Provider Service Pricing
  allProviderServicePricings: ProviderServicePricing[];
  activeProviderServicePricings: ProviderServicePricing[];
  loadingProviderServicePricings: boolean;
  loadedProviderServicePricings: boolean;
  savingProviderServicePricing: boolean;
  savedProviderServicePricing: boolean;

  // Transportation Services
  allTransportationServices: TransportationService[];
  activeTransportationServices: TransportationService[];
  loadingTransportationServices: boolean;
  loadedTransportationServices: boolean;

  // Transportation Services
  allTransportationAttributes: TransportationAttribute[];
  loadingTransportationAttributes: boolean;
  loadedTransportationAttributes: boolean;

  // Trip Leg Pricings
  tripLegPricings: TripLegPricings | null;
  loadingTripLegPricings: boolean;
  loadedTripLegPricings: boolean;
}

export const initialState: AccountState = {
  // Pricing Functions
  allPricingFunctions: [],
  loadingPricingFunctions: false,
  loadedPricingFunctions: false,

  // Pricing Models
  allPricingModels: [],
  activePricingModels: [],
  loadingPricingModels: false,
  loadedPricingModels: false,
  savingPricingModel: false,
  savedPricingModel: false,

  // Provider Service Pricing
  allProviderServicePricings: [],
  activeProviderServicePricings: [],
  loadingProviderServicePricings: false,
  loadedProviderServicePricings: false,
  savingProviderServicePricing: false,
  savedProviderServicePricing: false,

  // Transportation Services
  allTransportationServices: [],
  activeTransportationServices: [],
  loadingTransportationServices: false,
  loadedTransportationServices: false,

  // Transportation Attributes
  allTransportationAttributes: [],
  loadingTransportationAttributes: false,
  loadedTransportationAttributes: false,

  // Trip Leg Pricings
  tripLegPricings: null,
  loadingTripLegPricings: false,
  loadedTripLegPricings: false,
};

export const accountSlice = createSlice({
  name: 'accounting',
  initialState,
  reducers: {
    // ===== Start Pricing Functions =====
    setAllPricingFunctions: (
      state: AccountState,
      action: { type: string; payload: PricingFunction[] },
    ) => {
      state.allPricingFunctions = action.payload;
    },
    setLoadingPricingFunctions: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPricingFunctions = action.payload;
    },
    setLoadedPricingFunctions: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPricingFunctions = action.payload;
    },
    // ===== End Pricing Functions =====

    // ===== Start Pricing Models =====
    setAllPricingModels: (
      state: AccountState,
      action: { type: string; payload: PricingModel[] },
    ) => {
      state.allPricingModels = action.payload;

      state.activePricingModels = action.payload.filter(
        (pm) => pm.state === 'active',
      );
    },
    setActivePricingModels: (
      state: AccountState,
      action: { type: string; payload: PricingModel[] },
    ) => {
      state.activePricingModels = action.payload;
    },
    setLoadingPricingModels: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPricingModels = action.payload;
    },
    setLoadedPricingModels: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPricingModels = action.payload;
    },
    setSavingPricingModel: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingPricingModel = action.payload;
    },
    setSavedPricingModel: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedPricingModel = action.payload;
    },
    createPricingModel: (
      state: AccountState,
      action: { type: string; payload: PricingModel },
    ) => {
      state.allPricingModels.push(action.payload);
    },
    updatePricingModel: (
      state: AccountState,
      action: { type: string; payload: PricingModel },
    ) => {
      const index = state.allPricingModels.findIndex(
        (pm) => pm.pricingModelUUID === action.payload.pricingModelUUID,
      );
      state.allPricingModels[index] = action.payload;
    },
    deletePricingModel: (
      state: AccountState,
      action: { type: string; payload: string },
    ) => {
      state.allPricingModels = state.allPricingModels.filter(
        (pm) => pm.pricingModelUUID !== action.payload,
      );
    },
    // ===== End Pricing Models =====

    // ===== Start Provider Service Pricing =====
    setAllProviderServicePricings: (
      state: AccountState,
      action: { type: string; payload: ProviderServicePricing[] },
    ) => {
      state.allProviderServicePricings = action.payload;
    },
    setActiveProviderServicePricings: (
      state: AccountState,
      action: { type: string; payload: ProviderServicePricing[] },
    ) => {
      state.activeProviderServicePricings = action.payload;
    },
    setLoadingProviderServicePricings: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderServicePricings = action.payload;
    },
    setLoadedProviderServicePricings: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderServicePricings = action.payload;
    },
    setSavingProviderServicePricing: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingProviderServicePricing = action.payload;
    },
    setSavedProviderServicePricing: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedProviderServicePricing = action.payload;
    },
    createProviderServicePricing: (
      state: AccountState,
      action: { type: string; payload: ProviderServicePricing },
    ) => {
      state.allProviderServicePricings.push(action.payload);
    },
    updateProviderServicePricing: (
      state: AccountState,
      action: { type: string; payload: ProviderServicePricing },
    ) => {
      const index = state.allProviderServicePricings.findIndex(
        (psp) =>
          psp.providerServicePricingUUID ===
          action.payload.providerServicePricingUUID,
      );
      state.allProviderServicePricings[index] = action.payload;
    },
    deleteProviderServicePricing: (
      state: AccountState,
      action: { type: string; payload: string },
    ) => {
      state.allProviderServicePricings =
        state.allProviderServicePricings.filter(
          (psp) => psp.providerServicePricingUUID !== action.payload,
        );
    },
    // ===== End Provider Service Pricing =====

    // ===== Start Transportation Services =====
    setAllTransportationServices: (
      state: AccountState,
      action: { type: string; payload: TransportationService[] },
    ) => {
      state.allTransportationServices = action.payload;
    },
    setActiveTransportationServices: (
      state: AccountState,
      action: { type: string; payload: TransportationService[] },
    ) => {
      state.activeTransportationServices = action.payload;
    },
    setLoadingTransportationServices: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationServices = action.payload;
    },
    setLoadedTransportationServices: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationServices = action.payload;
    },

    // ===== Start Transportation Attributes =====
    setAllTransportationAttributes: (
      state: AccountState,
      action: { type: string; payload: TransportationAttribute[] },
    ) => {
      state.allTransportationAttributes = action.payload;
    },
    setLoadingTransportationAttributes: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTransportationAttributes = action.payload;
    },
    setLoadedTransportationAttributes: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTransportationAttributes = action.payload;
    },

    setTripLegPricings: (
      state: AccountState,
      action: { type: string; payload: TripLegPricings | null },
    ) => {
      state.tripLegPricings = action.payload;
    },
    setLoadingTripLegPricings: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingTripLegPricings = action.payload;
    },
    setLoadedTripLegPricings: (
      state: AccountState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedTripLegPricings = action.payload;
    },
  },
});

// ========================================
// BEGIN REDUX ACTIONS EXPORT
// ========================================

// Pricing Functions
export const { setAllPricingFunctions } = accountSlice.actions;
export const { setLoadingPricingFunctions } = accountSlice.actions;
export const { setLoadedPricingFunctions } = accountSlice.actions;

// Pricing Models
export const { setAllPricingModels } = accountSlice.actions;
export const { setActivePricingModels } = accountSlice.actions;
export const { setLoadingPricingModels } = accountSlice.actions;
export const { setLoadedPricingModels } = accountSlice.actions;
export const { setSavingPricingModel } = accountSlice.actions;
export const { setSavedPricingModel } = accountSlice.actions;
export const { createPricingModel } = accountSlice.actions;
export const { updatePricingModel } = accountSlice.actions;
export const { deletePricingModel } = accountSlice.actions;

// Provider Service Pricing
export const { setAllProviderServicePricings } = accountSlice.actions;
export const { setActiveProviderServicePricings } = accountSlice.actions;
export const { setLoadingProviderServicePricings } = accountSlice.actions;
export const { setLoadedProviderServicePricings } = accountSlice.actions;
export const { setSavingProviderServicePricing } = accountSlice.actions;
export const { setSavedProviderServicePricing } = accountSlice.actions;
export const { createProviderServicePricing } = accountSlice.actions;
export const { updateProviderServicePricing } = accountSlice.actions;
export const { deleteProviderServicePricing } = accountSlice.actions;

// Transportation Services
export const { setAllTransportationServices } = accountSlice.actions;
export const { setActiveTransportationServices } = accountSlice.actions;
export const { setLoadingTransportationServices } = accountSlice.actions;
export const { setLoadedTransportationServices } = accountSlice.actions;

// Transportation Attributes
export const { setAllTransportationAttributes } = accountSlice.actions;
export const { setLoadingTransportationAttributes } = accountSlice.actions;
export const { setLoadedTransportationAttributes } = accountSlice.actions;

// Trip Leg Pricings
export const { setTripLegPricings } = accountSlice.actions;
export const { setLoadingTripLegPricings } = accountSlice.actions;
export const { setLoadedTripLegPricings } = accountSlice.actions;

// ========================================
// END REDUX ACTIONS EXPORT
// ========================================

export const fetchPricingFunctionsAsync =
  (states?: AccountingState[]) => (dispatch: Function) => {
    dispatch(setLoadingPricingFunctions(true));
    dispatch(setLoadedPricingFunctions(false));
    accountingService.getPricingFunctions(states).subscribe({
      next: (pricingFunctions) => {
        dispatch(setAllPricingFunctions(pricingFunctions));
        dispatch(setLoadingPricingFunctions(false));
        dispatch(setLoadedPricingFunctions(true));
      },
      error: (err) => {
        dispatch(setLoadingPricingFunctions(false));
        dispatch(setLoadedPricingFunctions(false));
      },
    });
  };

export const fetchPricingModelsAsync =
  (states?: AccountingState[]) => (dispatch: Function) => {
    dispatch(setLoadingPricingModels(true));
    dispatch(setLoadedPricingModels(false));
    accountingService.getPricingModels(states).subscribe({
      next: (pricingModels) => {
        dispatch(setAllPricingModels(pricingModels));
        dispatch(setLoadingPricingModels(false));
        dispatch(setLoadedPricingModels(true));
      },
      error: (err) => {
        dispatch(setLoadingPricingModels(false));
        dispatch(setLoadedPricingModels(false));
      },
    });
  };

export const fetchProviderServicePricingsAsync =
  (transportationProviderID?: number, states?: AccountingState[]) =>
  (dispatch: Function) => {
    dispatch(setLoadingProviderServicePricings(true));
    dispatch(setLoadedProviderServicePricings(false));
    accountingService
      .getProviderServicePricings(transportationProviderID, states)
      .subscribe({
        next: (providerServicePricings) => {
          dispatch(setAllProviderServicePricings(providerServicePricings));
          dispatch(setLoadingProviderServicePricings(false));
          dispatch(setLoadedProviderServicePricings(true));
        },
        error: (err) => {
          dispatch(setLoadingProviderServicePricings(false));
          dispatch(setLoadedProviderServicePricings(false));
        },
      });
  };

export const createProviderServicePricingAsync =
  (providerServicePricing: CreatableProviderServicePricing) =>
  (dispatch: Function) => {
    dispatch(setSavingProviderServicePricing(true));
    dispatch(setSavedProviderServicePricing(false));
    accountingService
      .createProviderServicePricing(providerServicePricing)
      .subscribe({
        next: (providerServicePricing) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.createProviderServicePricingSuccess',
            ),
          );
          dispatch(createProviderServicePricing(providerServicePricing));
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(true));
        },
        error: (err) => {
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(false));
          dispatch(
            uiSlice.setErrorMessage(
              err.message || 'snackbar.createProviderServicePricingError',
            ),
          );
        },
      });
  };

export const updateProviderServicePricingAsync =
  (providerServicePricing: EditableProviderServicePricing) =>
  (dispatch: Function) => {
    dispatch(setSavingProviderServicePricing(true));
    dispatch(setSavedProviderServicePricing(false));
    accountingService
      .updateProviderServicePricing(providerServicePricing)
      .subscribe({
        next: (providerServicePricing) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.updateProviderServicePricingSuccess',
            ),
          );
          dispatch(updateProviderServicePricing(providerServicePricing));
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(true));
        },
        error: (err) => {
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(false));
          dispatch(
            uiSlice.setErrorMessage(
              err.message || 'snackbar.updateProviderServicePricingError',
            ),
          );
        },
      });
  };

export const updateProviderServicePricingEffectiveDatesAsync =
  (
    providerServicePricingUUID: string,
    effectiveStartDate?: string,
    effectiveEndDate?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setSavingProviderServicePricing(true));
    dispatch(setSavedProviderServicePricing(false));
    accountingService
      .updateProviderServicePricingEffectiveDates(
        providerServicePricingUUID,
        effectiveStartDate,
        effectiveEndDate,
      )
      .subscribe({
        next: (providerServicePricing) => {
          dispatch(
            uiSlice.setSuccessMessage(
              providerServicePricing.state === AccountingState.Active
                ? 'snackbar.activateProviderServicePricingSuccess'
                : 'snackbar.deactivateProviderServicePricingSuccess',
            ),
          );
          dispatch(updateProviderServicePricing(providerServicePricing));
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(true));
        },
        error: (err) => {
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(false));
        },
      });
  };

export const deleteProviderServicePricingAsync =
  (providerServicePricingUUID: string) => (dispatch: Function) => {
    dispatch(setSavingProviderServicePricing(true));
    dispatch(setSavedProviderServicePricing(false));
    accountingService
      .deleteProviderServicePricing(providerServicePricingUUID)
      .subscribe({
        next: (providerServicePricing) => {
          dispatch(
            uiSlice.setSuccessMessage(
              'snackbar.deleteProviderServicePricingSuccess',
            ),
          );
          dispatch(deleteProviderServicePricing(providerServicePricingUUID));
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(true));
        },
        error: (err) => {
          dispatch(setSavingProviderServicePricing(false));
          dispatch(setSavedProviderServicePricing(false));
        },
      });
  };

export const fetchTransportationServicesAsync =
  (states?: AccountingState[]) => (dispatch: Function) => {
    dispatch(setLoadingTransportationServices(true));
    dispatch(setLoadedTransportationServices(false));
    accountingService.getTransportationServices(states).subscribe({
      next: (transportationServices) => {
        dispatch(setAllTransportationServices(transportationServices));
        dispatch(setLoadingTransportationServices(false));
        dispatch(setLoadedTransportationServices(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationServices(false));
        dispatch(setLoadedTransportationServices(false));
      },
    });
  };

export const fetchTransportationAttributesAsync =
  (states?: AccountingState[]) => (dispatch: Function) => {
    dispatch(setLoadingTransportationAttributes(true));
    dispatch(setLoadedTransportationAttributes(false));
    accountingService.getTransportationAttributes(states).subscribe({
      next: (transportationAttributes) => {
        dispatch(setAllTransportationAttributes(transportationAttributes));
        dispatch(setLoadingTransportationAttributes(false));
        dispatch(setLoadedTransportationAttributes(true));
      },
      error: (err) => {
        dispatch(setLoadingTransportationAttributes(false));
        dispatch(setLoadedTransportationAttributes(false));
      },
    });
  };

export const fetchTripLegPricingsAsync =
  (
    passengerUUID: string,
    tripLegReferenceID: string,
    filter?: 'client' | 'provider',
    tripLegState?: 'active' | 'inactive',
  ) =>
  (dispatch: Function) => {
    dispatch(setLoadingTripLegPricings(true));
    dispatch(setLoadedTripLegPricings(false));
    accountingService
      .getTripLegPricings(
        passengerUUID,
        tripLegReferenceID,
        filter,
        tripLegState,
      )
      .subscribe({
        next: (pricings) => {
          dispatch(setTripLegPricings(pricings));
          dispatch(setLoadingTripLegPricings(false));
          dispatch(setLoadedTripLegPricings(true));
        },
        error: (err) => {
          dispatch(setLoadingTripLegPricings(false));
          dispatch(setLoadedTripLegPricings(false));
        },
      });
  };

// Pricing Functions
export const selectAllPricingFunctions = (state: {
  accounting: AccountState;
}) => state.accounting.allPricingFunctions;
export const selectLoadingPricingFunctions = (state: {
  accounting: AccountState;
}) => state.accounting.loadingPricingFunctions;
export const selectLoadedPricingFunctions = (state: {
  accounting: AccountState;
}) => state.accounting.loadedPricingFunctions;

// Pricing Models
export const selectAllPricingModels = (state: { accounting: AccountState }) =>
  state.accounting.allPricingModels;
export const selectActivePricingModels = (state: {
  accounting: AccountState;
}) => state.accounting.activePricingModels;
export const selectLoadingPricingModels = (state: {
  accounting: AccountState;
}) => state.accounting.loadingPricingModels;
export const selectLoadedPricingModels = (state: {
  accounting: AccountState;
}) => state.accounting.loadedPricingModels;
export const selectSavingPricingModel = (state: { accounting: AccountState }) =>
  state.accounting.savingPricingModel;
export const selectSavedPricingModel = (state: { accounting: AccountState }) =>
  state.accounting.savedPricingModel;

// Provider Service Pricing
export const selectAllProviderServicePricings = (state: {
  accounting: AccountState;
}) => state.accounting.allProviderServicePricings;
export const selectActiveProviderServicePricings = (state: {
  accounting: AccountState;
}) => state.accounting.activeProviderServicePricings;
export const selectLoadingProviderServicePricings = (state: {
  accounting: AccountState;
}) => state.accounting.loadingProviderServicePricings;
export const selectLoadedProviderServicePricings = (state: {
  accounting: AccountState;
}) => state.accounting.loadedProviderServicePricings;
export const selectSavingProviderServicePricing = (state: {
  accounting: AccountState;
}) => state.accounting.savingProviderServicePricing;
export const selectSavedProviderServicePricing = (state: {
  accounting: AccountState;
}) => state.accounting.savedProviderServicePricing;

// Transportation Services
export const selectAllTransportationServices = (state: {
  accounting: AccountState;
}) => state.accounting.allTransportationServices;
export const selectActiveTransportationServices = (state: {
  accounting: AccountState;
}) => state.accounting.activeTransportationServices;
export const selectLoadingTransportationServices = (state: {
  accounting: AccountState;
}) => state.accounting.loadingTransportationServices;

// Transportation Attributes
export const selectAllTransportationAttributes = (state: {
  accounting: AccountState;
}) => state.accounting.allTransportationAttributes;
export const selectLoadingTransportationAttributes = (state: {
  accounting: AccountState;
}) => state.accounting.loadingTransportationAttributes;

// Pricing Functions
export const selectTripLegPricings = (state: { accounting: AccountState }) =>
  state.accounting.tripLegPricings;
export const selectLoadingTripLegPricings = (state: {
  accounting: AccountState;
}) => state.accounting.loadingTripLegPricings;
export const selectLoadedTripLegPricings = (state: {
  accounting: AccountState;
}) => state.accounting.loadedTripLegPricings;

export default accountSlice.reducer;
